<template>
  <div v-if="tagDefs.length" class="intake-tag-bar">
    <span class="intake-tag-bar__label">
      <v-icon size="14">mdi-tag-outline</v-icon>
      Tags
    </span>
    <TagChips
      :tags="tags"
      :tag-defs="tagDefs"
      :tag-due-dates="tagDueDates"
      size="md"
      removable
      @update:tags="onTagsChanged"
      @update:tag-due-dates="onDueDatesChanged" />
    <TagPicker
      :tags="tags"
      :tag-defs="tagDefs"
      :tag-due-dates="tagDueDates"
      :sort-order="sortOrder"
      size="md"
      @update:tags="onTagsChanged"
      @update:tag-due-dates="onDueDatesChanged" />
  </div>
</template>

<script setup>
import TagChips from "@/components/dashboard/TagChips.vue";
import TagPicker from "@/components/dashboard/TagPicker.vue";
import { intakeService } from "@/services/intakeService";
import { orderInfoService } from "@/services/orderInfoService";
import { filterUserTags } from "@/utils/tags";

const props = defineProps({
  tags: { type: Array, default: () => [] },
  tagDefs: { type: Array, default: () => [] },
  tagDueDates: { type: Array, default: () => [] },
  sortOrder: { type: String, default: null },
  entityType: { type: String, required: true, validator: (v) => ["document", "order", "submission"].includes(v) },
  entityId: { type: [String, Number], required: true },
});

const emit = defineEmits(["update:tags", "update:tagDueDates", "saved"]);

function saveTags(tags, tagDueDates) {
  if (props.entityType === "submission") {
    return orderInfoService.updateSubmissionTags(props.entityId, tags, tagDueDates);
  }
  if (props.entityType === "document") {
    return intakeService.updateDocumentTags(props.entityId, tags, tagDueDates);
  }
  return intakeService.updateOrderTags(props.entityId, tags, tagDueDates);
}

function adoptResponse(response) {
  if (Array.isArray(response?.tags)) emit("update:tags", response.tags);
  if (Array.isArray(response?.tagDueDates)) emit("update:tagDueDates", response.tagDueDates);
}

async function onTagsChanged(newTags) {
  const oldTags = [...props.tags];
  emit("update:tags", newTags);
  try {
    adoptResponse(await saveTags(newTags));
    emit("saved");
  } catch {
    emit("update:tags", oldTags);
  }
}

async function onDueDatesChanged(newDueDates) {
  const oldDueDates = [...props.tagDueDates];
  emit("update:tagDueDates", newDueDates);
  try {
    adoptResponse(await saveTags(filterUserTags(props.tags, props.tagDefs), newDueDates));
    emit("saved");
  } catch {
    emit("update:tagDueDates", oldDueDates);
  }
}
</script>

<style lang="scss" scoped>
.intake-tag-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 14px 2px;
}

.intake-tag-bar__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: $lh_font_family_dm_sans;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
  white-space: nowrap;
}
</style>
